import usePageSEO from '../hooks/usePageSEO';
import { ProductSchema } from '../components/JsonLd';

import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import { 
  ArrowRight, 
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Zap,
  Clock,
  DollarSign,
  Shield
} from 'lucide-react';
import { Button } from '../components/ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_64eb10e1-fdde-4977-afc2-4761fbf5691b/artifacts/5pkoywp1_Plouton_Logo%20%281%29.png";
const CAL_LINK = "https://cal.com/muhammad-furqan-karim-kidwai-uvsohz/30min";

const comparisonData = [
  {
    feature: 'Integration Requirements',
    plouton: 'Zero integrations needed—works directly in existing systems',
    rpa: 'Requires API connections or system-level integrations',
    ploutonBetter: true,
  },
  {
    feature: 'Implementation Time',
    plouton: 'Days to weeks',
    rpa: 'Months to years',
    ploutonBetter: true,
  },
  {
    feature: 'Handling Variations',
    plouton: 'AI adapts to variations in data and layouts automatically',
    rpa: 'Breaks when encountering unexpected variations',
    ploutonBetter: true,
  },
  {
    feature: 'Maintenance Burden',
    plouton: 'Self-healing—adapts to UI changes automatically',
    rpa: 'Requires constant maintenance when systems update',
    ploutonBetter: true,
  },
  {
    feature: 'Unstructured Data',
    plouton: 'Processes any document format with AI understanding',
    rpa: 'Limited to structured, predictable data formats',
    ploutonBetter: true,
  },
  {
    feature: 'Decision Making',
    plouton: 'Makes intelligent decisions based on context',
    rpa: 'Follows rigid, pre-programmed rules only',
    ploutonBetter: true,
  },
  {
    feature: 'Exception Handling',
    plouton: 'Reasons through exceptions and escalates appropriately',
    rpa: 'Stops and requires human intervention',
    ploutonBetter: true,
  },
  {
    feature: 'Audit Trail',
    plouton: 'Complete screen recordings and decision explanations',
    rpa: 'Basic logging of actions taken',
    ploutonBetter: true,
  },
];

const benefits = [
  {
    icon: Clock,
    title: '10x Faster Implementation',
    description: 'Go live in days, not months. No IT projects, no system changes, no waiting.',
  },
  {
    icon: DollarSign,
    title: '75% Lower TCO',
    description: 'No integration costs, no maintenance overhead, no specialized developers needed.',
  },
  {
    icon: Zap,
    title: 'True Intelligence',
    description: 'AI that understands context, handles variations, and makes decisions—not just follows scripts.',
  },
  {
    icon: Shield,
    title: 'Enterprise Security',
    description: 'SOC 2 Type II certified with complete audit trails and bank-grade encryption.',
  },
];

export const ComparisonPage = () => {
  usePageSEO({
    title: 'Plouton AI vs RPA: Comparing Financial Automation Tools',
    description: 'Compare traditional RPA with Plouton AI\'s finance automation tool. See why our browser agents offer a simpler solution for your daily operations. Learn more.',
    path: '/comparisons/plouton-vs-traditional-rpa'
  });

  return (
    <div className="min-h-screen bg-white pt-[108px]">
      <ProductSchema
        name="Plouton AI vs Traditional RPA"
        description="Compare browser-native AI agents to legacy RPA tools. No APIs, no broken bots when interfaces change."
        url="https://plouton.co/comparisons/plouton-vs-traditional-rpa"
      />
      {/* Breadcrumb */}
      <div className="bg-slate-50 border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-4">
          <div className="flex items-center gap-2 text-sm">
            <Link to="/resources" className="text-slate-500 hover:text-slate-900 transition-colors">
              Resources
            </Link>
            <span className="text-slate-400">/</span>
            <span className="text-slate-500">Comparisons</span>
            <span className="text-slate-400">/</span>
            <span className="text-slate-900">Plouton vs Traditional RPA</span>
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="px-3 py-1 bg-purple-100 text-purple-700 text-sm font-medium rounded-full">
              Comparison
            </span>

            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-slate-900 mt-6 mb-6">
              Plouton AI vs Traditional RPA
            </h1>

            <p className="text-xl text-slate-600 leading-relaxed max-w-2xl mx-auto">
              See why AI-powered browser agents are replacing traditional RPA tools 
              for finance automation. Faster implementation, lower costs, and true intelligence.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-heading text-3xl font-bold text-slate-900 mb-4">
              Feature-by-Feature Comparison
            </h2>
          </motion.div>

          <motion.div
            className="bg-white rounded-2xl border border-slate-200 overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {/* Header */}
            <div className="grid grid-cols-3 bg-slate-50 border-b border-slate-200">
              <div className="p-4 font-semibold text-slate-900">Feature</div>
              <div className="p-4 font-semibold text-blue-600 text-center bg-blue-50">Plouton AI</div>
              <div className="p-4 font-semibold text-slate-500 text-center">Traditional RPA</div>
            </div>

            {/* Rows */}
            {comparisonData.map((row, index) => (
              <div 
                key={row.feature} 
                className={`grid grid-cols-3 border-b border-slate-100 last:border-b-0 ${
                  index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'
                }`}
              >
                <div className="p-4 font-medium text-slate-900">{row.feature}</div>
                <div className="p-4 text-slate-700 bg-blue-50/50 flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>{row.plouton}</span>
                </div>
                <div className="p-4 text-slate-500 flex items-start gap-2">
                  <XCircle className="w-5 h-5 text-slate-300 flex-shrink-0 mt-0.5" />
                  <span>{row.rpa}</span>
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-heading text-3xl font-bold text-white mb-4">
              Why Teams Choose Plouton AI
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              The benefits of AI-powered automation over traditional RPA approaches.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                className="bg-slate-800/50 rounded-2xl p-8 border border-slate-700"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center mb-4">
                  <benefit.icon className="w-6 h-6 text-blue-400" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-white mb-2">
                  {benefit.title}
                </h3>
                <p className="text-slate-400">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* The Bottom Line */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-heading text-3xl font-bold text-slate-900 mb-6 text-center">
              The Bottom Line
            </h2>
            <div className="bg-slate-50 rounded-2xl p-8 sm:p-10">
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                Traditional RPA was built for a world of stable, predictable systems. But finance 
                operations involve constant variation—different invoice formats, changing vendor 
                portals, evolving compliance requirements.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                <strong className="text-slate-900">Plouton AI agents are built for this reality.</strong> They 
                understand context, adapt to changes, and make intelligent decisions—all while 
                maintaining the audit trails and controls that finance teams require.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                The result? Automation that actually works in the real world, implemented in days 
                instead of months, at a fraction of the cost.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <h2 className="font-heading text-3xl font-bold text-slate-900 mb-4">
            Ready to move beyond traditional RPA?
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            See how Plouton AI compares for your specific use cases.
          </p>
          <a href={CAL_LINK} target="_blank" rel="noopener noreferrer">
            <Button className="bg-blue-600 text-white hover:bg-blue-700 px-8 py-5">
              Book a Demo
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </a>
        </div>
      </section>

      {/* Back link */}
      <section className="py-8 bg-white border-t border-slate-100">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <Link 
            to="/resources" 
            className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Resources
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ComparisonPage;
